
import React, { useContext, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

// Components

import SubHeader from '@components/ssap/headers/SubHeader';
import SummaryItem from '@components/ssap/pages/pageIncludes/summary/SummaryItem';

// Contexts

import { Context } from '@contexts/ssapSummaryContext';

// Hooks

import { useSummaryItemDiffRegister } from '@hooks/useSummaryItemDiffRegister';

// Utils

import { currencyTools } from '@utils/tools/currencyTools';
import { dateMethods } from '@utils/dates/dateMethods';
import { getFrequencyId } from '@utils/householdMember/getFrequencyId';
import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { phoneNumberTools } from '@utils/tools/phoneNumberTools';
import { isEqual, pick } from '@utils/miscellaneous/objectHelpers';
import { parseDate } from '@utils/dates/date.utils';

// Constants

import { SSAP_DATE_FORMATS } from '@constants/ssap/miscellaneous/miscellaneous';


const HraDetails = props => {
    const {
        applicantGuid,
        healthReimbursementArrangement,
        householdMembers,
        isFinalSummaryPage,
        isSsapInReviewMode,
        previousData
    } = props;

    const intlApi = useIntl();

    const summaryContext = useContext(Context);

    const {
        summaryItemsComparisonEnabled
    } = summaryContext;

    const previousHealthReimbursementArrangement = useMemo(
        () => {
            const index = previousData.taxHousehold.householdMember.findIndex(hhm => hhm.applicantGuid === applicantGuid);
            if(~index) {
                const {
                    healthReimbursementArrangement
                } = previousData.taxHousehold.householdMember[index];
                return healthReimbursementArrangement;
            }
            return [];
        },
        [ previousData ]
    );

    const [
        arePreviousHraDetailsShown,
        toggleHraDetailsVisibility
    ] = useState(!previousHealthReimbursementArrangement.length);

    const handleToggleLinkClick = e => {
        e.preventDefault();
        toggleHraDetailsVisibility(isShown => !isShown);
    };

    const getArrangements = (arrangements = [], arePrevious = false) => {
        if (arrangements.length === 0 && arePrevious) {
            return (
                <div className = 'grid-row' >
                    <FormattedMessage id = 'common.no_text' />
                </div>
            );
        }

        return arrangements.map((hra, index) => {
            const {
                applicantGuid,
                enrolledInIchraIndicator,
                employerName,
                employerPhoneNumber,
                employeeSelfOnlyOfferAmount,
                employeeSelfOnlyOfferFrequencyType,
                hraType = '',
                startDate
            } = hra;

            const resolvedHouseholdMembers = arePrevious
                ? previousData.taxHousehold.householdMember
                : householdMembers;

            const employeeName = householdMemberTools
                .extractFromHouseholdMembers(resolvedHouseholdMembers)
                .getFullNameByHhmGuid(applicantGuid);

            const getCombinedValue = (employeeSelfOnlyOfferAmount, employeeSelfOnlyOfferFrequencyType) => {
                const formattedHraAmount = currencyTools
                    .getMoneyFormat(employeeSelfOnlyOfferAmount, true);

                const formattedFrequency = intlApi.formatMessage({
                    id: getFrequencyId(
                        employeeSelfOnlyOfferFrequencyType,
                        true as any,
                    ),
                });

                return `${ formattedHraAmount } / ${ formattedFrequency }`;
            };

            return (
                <div
                    className = 'grid-col-12'
                    key = { index }
                >
                    <div className = 'review-document grid-row' >
                        <div className = 'grid-col-12 margin-y-0' >
                            <div className = 'grid-row margin-bottom-3' >
                                <SummaryItem
                                    textCode = 'common.employer_name'
                                    ignoreIntl
                                    isDiffSwitchingIgnored
                                    value = { employerName }
                                    isStrikeThroughValue = { arePrevious }
                                />
                                <SummaryItem
                                    textCode = 'common.employee_name'
                                    ignoreIntl
                                    isDiffSwitchingIgnored
                                    value = { employeeName }
                                    isStrikeThroughValue = { arePrevious }
                                />
                                <SummaryItem
                                    textCode = 'common.contact_number'
                                    ignoreIntl
                                    isDiffSwitchingIgnored
                                    value = { phoneNumberTools.getAdjustedPhoneNumber(employerPhoneNumber) }
                                    isStrikeThroughValue = { arePrevious }
                                />
                                <SummaryItem
                                    textCode = 'common.hra_start_date'
                                    ignoreIntl
                                    isDiffSwitchingIgnored
                                    value = {
                                        dateMethods.getFormattedDate(
                                            SSAP_DATE_FORMATS.HRA_START_DATE,
                                            parseDate(startDate)
                                        )
                                    }
                                    isStrikeThroughValue = { arePrevious }
                                />
                                <SummaryItem
                                    textCode = 'common.hra_type'
                                    isDiffSwitchingIgnored
                                    value = { `common.hra_type_${ hraType.toLowerCase() }` }
                                    isStrikeThroughValue = { arePrevious }
                                />
                                <SummaryItem
                                    textCode = 'common.enrolled_in_hra'
                                    isDiffSwitchingIgnored
                                    value = { enrolledInIchraIndicator }
                                    isStrikeThroughValue = { arePrevious }
                                />
                                <SummaryItem
                                    textCode = 'common.maximum_reimbursement_amount'
                                    ignoreIntl
                                    isDiffSwitchingIgnored
                                    value = { getCombinedValue(employeeSelfOnlyOfferAmount, employeeSelfOnlyOfferFrequencyType) }
                                    isStrikeThroughValue = { arePrevious }
                                />
                            </div>
                        </div>
                    </div>
                </div>
            );
        });
    };

    const toggleLink = (
        <a
            href = '#'
            onClick = { handleToggleLinkClick }
            className = 'instruction_link instruction_link_p'
        >
            <FormattedMessage id = 'ssap.summary.show_previous_values_link' />
        </a>
    );

    const filterHraCoverageProps = (coverages = []) => {
        return coverages.map(coverage => {
            return pick(
                coverage,
                [
                    'enrolledInIchraIndicator',
                    'employerName',
                    'employerPhoneNumber',
                    'employeeSelfOnlyOfferAmount',
                    'hraType',
                    'startDate'
                ]
            );
        });
    };

    const diffs = useMemo(
        () => {
            return {
                status: !isEqual(
                    filterHraCoverageProps(healthReimbursementArrangement),
                    filterHraCoverageProps(previousHealthReimbursementArrangement)
                )
            };
        },
        [ healthReimbursementArrangement, previousHealthReimbursementArrangement ]
    );


    const linkProps =  (
        summaryItemsComparisonEnabled
        && diffs.status
        && previousHealthReimbursementArrangement.length
    )
        ? {
            auxText: 'common.non_internationalized_text',
            auxTextReplacements: { text: toggleLink }
        }
        : { };

    useSummaryItemDiffRegister({
        diffs: { status: healthReimbursementArrangement.length === 0 ? false : diffs.status },
        summaryContext,
        values: {
            category: 'hra',
            value: healthReimbursementArrangement,
            previousValue: previousHealthReimbursementArrangement,
            textCode: applicantGuid
        }
    });

    if(healthReimbursementArrangement.length === 0) {
        return null;
    }

    // We highlight background only on Final Review page (SSAP in edit mode) if we are tracking a diff
    const displayUpdatedRow = (diffs.status && isFinalSummaryPage && !isSsapInReviewMode)
        ? 'bg-updated_fields'
        : '';

    return (
        <div className = 'grid-col-12' >
            <div className = 'summary__header'>
                <SubHeader
                    { ...{
                        headerClassName: 'gi-heading-summary margin-bottom-1',
                        text: 'ssap.subheader.hra_details',
                        dataTestId:`HRA-Info-Header-${applicantGuid}`,
                        ...linkProps
                    } }
                />
            </div>
            <div className = { displayUpdatedRow } >
                {
                    arePreviousHraDetailsShown && getArrangements(previousHealthReimbursementArrangement, true)
                }
                {
                    getArrangements(healthReimbursementArrangement)
                }
            </div>
        </div>
    );
};

export default HraDetails;